import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ExternalLink, Handshake, Send, ShieldAlert, TriangleAlert, UserX } from 'lucide-react';
import {
  CandidateSource,
  PoolStatus,
  type CandidateUpdate,
  type EnumDef,
  type FileKind,
  type PoolStatus as PoolStatusCode,
} from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { ActivityTimeline } from '@/components/data/ActivityTimeline';
import { FilesPanel } from '@/components/data/FilesPanel';
import { FollowUpList } from '@/components/data/FollowUpList';
import { PropertiesPanel, type PropertyField } from '@/components/data/PropertiesPanel';
import { TONE_CLASSES } from '@/components/data/tones';
import { EnumMenuBadge } from '@/pages/candidates/EnumMenuBadge';
import { GdprPanel } from '@/pages/candidates/GdprPanel';
import { LanguagesCard } from '@/pages/candidates/LanguagesCard';
import { ProfileVersionsPanel } from '@/pages/candidates/ProfileVersionsPanel';
import { ProposeDialog } from '@/pages/candidates/ProposeDialog';
import { RateNormaliserCard } from '@/pages/candidates/RateNormaliserCard';
import { SectionCard } from '@/pages/candidates/SectionCard';
import { SubmissionsTable } from '@/pages/candidates/SubmissionsTable';
import { SummaryCard } from '@/pages/candidates/SummaryCard';
import {
  DEFAULT_HOURS_PER_DAY,
  DEFAULT_RETENTION_MONTHS,
  driveFolderUrl,
  useCandidate,
  useInvalidateCandidates,
  useOptions,
  useSettings,
  useSuppliers,
  useUpdateCandidate,
} from '@/pages/candidates/api';

const CV_KINDS: FileKind[] = ['CV_ORIGINAL', 'OTHER'];

export function CandidateDetailPage() {
  const { id = '' } = useParams();
  const { canWrite, isAdmin } = useAuth();
  const candidateQuery = useCandidate(id);
  const update = useUpdateCandidate(id);
  const invalidate = useInvalidateCandidates();
  const settings = useSettings();
  const options = useOptions();
  const suppliers = useSuppliers();
  const [proposeOpen, setProposeOpen] = React.useState(false);

  const candidate = candidateQuery.data;
  const hoursPerDay = settings.data?.hours_per_day ?? DEFAULT_HOURS_PER_DAY;
  const retentionMonths = settings.data?.consent_retention_months ?? DEFAULT_RETENTION_MONTHS;
  const refetch = () => void candidateQuery.refetch();
  const patch = (p: CandidateUpdate) => update.mutateAsync(p);

  const breadcrumbs = [{ label: 'Candidates', to: '/candidates' }, { label: candidate?.candidateId ?? id }];

  if (candidateQuery.isLoading || (!candidate && !candidateQuery.isError)) {
    return (
      <>
        <PageHeader title="Candidate" breadcrumbs={breadcrumbs} />
        <SkeletonLines lines={8} />
      </>
    );
  }
  if (!candidate) {
    return (
      <>
        <PageHeader title="Candidate" breadcrumbs={breadcrumbs} />
        <EmptyState
          icon={UserX}
          title="Candidate not found"
          description={
            candidateQuery.error instanceof Error
              ? candidateQuery.error.message
              : `No candidate matches "${id}".`
          }
          action={
            <Link to="/candidates" className={buttonVariants({ variant: 'outline' })}>
              Back to candidates
            </Link>
          }
        />
      </>
    );
  }

  const erased = Boolean(candidate.erasedAt);
  const canEdit = canWrite && !erased;
  const countryOptions = options.data?.country ?? [];
  const supplierOptions = (suppliers.data ?? []).map((p) => ({ value: p.id, label: p.name }));
  if (candidate.supplier && !supplierOptions.some((o) => o.value === candidate.supplier?.id)) {
    supplierOptions.push({ value: candidate.supplier.id, label: candidate.supplier.name });
  }

  const fields: PropertyField[] = [
    {
      key: 'fullName',
      label: 'Full name',
      type: 'text',
      value: candidate.fullName,
      placeholder: 'Unknown (CV without a name)',
      help: 'Only "First S." ever appears in documents.',
    },
    { key: 'label', label: 'Label', type: 'text', value: candidate.label },
    { key: 'headline', label: 'Headline', type: 'text', value: candidate.headline },
    {
      key: 'skills',
      label: 'Skills',
      type: 'list',
      value: candidate.skills,
      help: 'Comma-separated when editing',
    },
    {
      key: 'poolStatus',
      label: 'Pool status',
      type: 'enum',
      value: candidate.poolStatus,
      def: PoolStatus as unknown as EnumDef<string>,
    },
    {
      key: 'source',
      label: 'Source',
      type: 'enum',
      value: candidate.source,
      def: CandidateSource as unknown as EnumDef<string>,
      allowEmpty: true,
    },
    {
      key: 'supplier',
      label: 'Supplier',
      type: 'select',
      value: candidate.supplierId,
      options: supplierOptions,
      allowEmpty: true,
    },
    countryOptions.length > 0
      ? {
          key: 'country',
          label: 'Country',
          type: 'select',
          value: candidate.country,
          options: [
            ...countryOptions.map((c) => ({ value: c, label: c })),
            ...(candidate.country && !countryOptions.includes(candidate.country)
              ? [{ value: candidate.country, label: candidate.country }]
              : []),
          ],
          allowEmpty: true,
        }
      : { key: 'country', label: 'Country', type: 'text', value: candidate.country },
    { key: 'city', label: 'City', type: 'text', value: candidate.city },
    {
      key: 'email',
      label: 'E-mail (internal)',
      type: 'text',
      value: candidate.email,
      help: 'Never appears in a profile',
    },
    {
      key: 'phone',
      label: 'Phone (internal)',
      type: 'text',
      value: candidate.phone,
      help: 'Never appears in a profile',
    },
    { key: 'availableFrom', label: 'Available from', type: 'date', value: candidate.availableFrom },
    { key: 'consentDate', label: 'Consent date', type: 'date', value: candidate.consentDate },
  ];

  const cvFiles = candidate.files.filter((f) => CV_KINDS.includes(f.kind as FileKind));
  const otherFiles = [
    ...candidate.files.filter((f) => !CV_KINDS.includes(f.kind as FileKind)),
    ...candidate.profileFiles,
  ];
  const description = [candidate.candidateId, candidate.label, candidate.headline]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={candidate.computed.displayName}
        description={description}
        meta={
          <>
            <EnumMenuBadge
              def={PoolStatus}
              value={candidate.poolStatus}
              onChange={(v: PoolStatusCode) =>
                patch({ poolStatus: v })
                  .then(() => toast.success(`Pool status → ${PoolStatus.label(v)}`))
                  .catch((e: Error) => toast.error(e.message))
              }
              readOnly={!canEdit}
              busy={update.isPending}
              label="Pool status"
            />
            {candidate.supplier ? (
              <Link
                to={`/partners/${candidate.supplier.id}`}
                className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Badge variant="outline" className="gap-1 font-normal">
                  <Handshake className="h-3 w-3" aria-hidden="true" />
                  {candidate.supplier.name}
                </Badge>
              </Link>
            ) : null}
            {erased ? (
              <Badge className={cn('gap-1', TONE_CLASSES.danger)}>
                <ShieldAlert className="h-3 w-3" aria-hidden="true" /> Erased {formatDate(candidate.erasedAt)}
              </Badge>
            ) : candidate.computed.consentExpiresSoon ? (
              <Badge className={cn('gap-1', TONE_CLASSES.warning)}>
                <TriangleAlert className="h-3 w-3" aria-hidden="true" /> Consent expires{' '}
                {formatDate(candidate.computed.consentExpiry)}
              </Badge>
            ) : null}
          </>
        }
        actions={
          canEdit ? (
            <Button onClick={() => setProposeOpen(true)}>
              <Send aria-hidden="true" /> Propose for position
            </Button>
          ) : null
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <SectionCard title="Properties">
            <PropertiesPanel
              fields={fields}
              onSave={(p) => patch(p as CandidateUpdate)}
              readOnly={!canEdit}
            />
          </SectionCard>

          <LanguagesCard
            languages={candidate.languages}
            canEdit={canEdit}
            suggestions={options.data?.language ?? []}
            onSave={(languages) => patch({ languages })}
          />

          <RateNormaliserCard
            rateText={candidate.rateExpectationText}
            rateEurH={candidate.rateExpectationEurH}
            hoursPerDay={hoursPerDay}
            canEdit={canEdit}
            onSave={(p) => patch(p)}
          />

          <SummaryCard
            summary={candidate.summary}
            canEdit={canEdit}
            onSave={(summary) => patch({ summary })}
          />

          <SectionCard
            title="CV files"
            description={
              candidate.driveFolderName ? (
                <>
                  Drive folder <span className="font-mono text-xs">{candidate.driveFolderName}</span>
                </>
              ) : (
                'Original CVs and other documents'
              )
            }
            actions={
              candidate.driveFolderId ? (
                <a
                  href={driveFolderUrl(candidate.driveFolderId)}
                  target="_blank"
                  rel="noreferrer"
                  className={buttonVariants({ variant: 'outline', size: 'sm' })}
                >
                  <ExternalLink aria-hidden="true" /> Open folder in Drive
                </a>
              ) : null
            }
          >
            <FilesPanel
              entityType="CANDIDATE"
              entityId={candidate.id}
              files={cvFiles}
              canEdit={canEdit}
              uploadKinds={CV_KINDS}
              defaultKind="CV_ORIGINAL"
              onChanged={refetch}
            />
          </SectionCard>

          <ProfileVersionsPanel
            candidate={candidate}
            canEdit={canEdit}
            isAdmin={isAdmin}
            onChanged={refetch}
          />

          <SectionCard
            title="Submissions"
            description="Positions this candidate was proposed for. Change the status inline; closed statuses ask for the outcome reason."
          >
            <SubmissionsTable submissions={candidate.submissions} canEdit={canWrite} />
          </SectionCard>

          <GdprPanel
            candidate={candidate}
            retentionMonths={retentionMonths}
            canWrite={canWrite}
            isAdmin={isAdmin}
            onPatch={(p) => patch(p)}
            onErased={() => void invalidate()}
          />
        </div>

        <aside className="min-w-0 space-y-6" aria-label="Candidate side panel">
          <SectionCard title="Activity">
            <ActivityTimeline
              entityType="CANDIDATE"
              entityId={candidate.id}
              entries={candidate.activity}
              canAdd={canWrite}
              onChanged={refetch}
            />
          </SectionCard>
          <SectionCard title="Open questions">
            <FollowUpList
              entityType="CANDIDATE"
              entityId={candidate.id}
              items={candidate.followUps}
              canEdit={canEdit}
              onChanged={refetch}
            />
          </SectionCard>
          <SectionCard title="Documents" description="Generated profiles and other linked files">
            <FilesPanel entityType="CANDIDATE" entityId={candidate.id} files={otherFiles} canEdit={false} />
          </SectionCard>
        </aside>
      </div>

      <ProposeDialog open={proposeOpen} onOpenChange={setProposeOpen} candidate={candidate} />
    </>
  );
}
